export const COMPETITIVE_RULESET = "sol-competitive-v2" as const;

export const COMBO_WINDOW_SECONDS = 1.5;
export const COMBO_MULTIPLIER_STEP = 0.1;
export const COMBO_MULTIPLIER_CAP = 3;
export const RARE_BONUS_POINTS = 5_000;
export const GOAL_BONUS_POINTS = 20_000;
export const TIME_BONUS_MAX_POINTS = 30_000;
export const TIME_BONUS_FULL_SECONDS = 290;
export const TIME_BONUS_ZERO_SECONDS = 720;

export type ResultRank = "S" | "A" | "B" | "C" | "D";

export interface PickupScore {
  readonly basePoints: number;
  readonly combo: number;
  readonly comboMultiplier: number;
  readonly rare: boolean;
  readonly delta: number;
  readonly totalScore: number;
}

export interface RunSnapshot {
  readonly ruleset: typeof COMPETITIVE_RULESET;
  readonly timeSeconds: number;
  readonly score: number;
  readonly pickupCount: number;
  readonly rareCount: number;
  readonly combo: number;
  readonly comboMultiplier: number;
  readonly finished: boolean;
}

export interface RunResult {
  readonly ruleset: typeof COMPETITIVE_RULESET;
  readonly timeSeconds: number;
  readonly rank: ResultRank;
  readonly pickupCount: number;
  readonly rareCount: number;
  readonly scoreBeforeBonuses: number;
  readonly goalBonus: number;
  readonly timeBonus: number;
  readonly score: number;
}

function requireNonNegativeFinite(value: number, name: string): number {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${name} must be a finite, non-negative number`);
  }
  return value;
}

export function rankForTime(timeSeconds: number): ResultRank {
  const time = requireNonNegativeFinite(timeSeconds, "timeSeconds");
  if (time <= 290) return "S";
  if (time <= 400) return "A";
  if (time <= 540) return "B";
  if (time <= 720) return "C";
  return "D";
}

export function timeBonusForTime(timeSeconds: number): number {
  const time = requireNonNegativeFinite(timeSeconds, "timeSeconds");
  if (time <= TIME_BONUS_FULL_SECONDS) return TIME_BONUS_MAX_POINTS;
  if (time >= TIME_BONUS_ZERO_SECONDS) return 0;

  const bonusRange = TIME_BONUS_ZERO_SECONDS - TIME_BONUS_FULL_SECONDS;
  const progress = (time - TIME_BONUS_FULL_SECONDS) / bonusRange;
  return Math.round(TIME_BONUS_MAX_POINTS * (1 - progress));
}

export class RunStats {
  private elapsedSeconds = 0;
  private totalScore = 0;
  private pickups = 0;
  private rares = 0;
  private comboCount = 0;
  private comboElapsedSeconds = Number.POSITIVE_INFINITY;
  private completedResult: Readonly<RunResult> | null = null;

  get timeSeconds(): number {
    return this.elapsedSeconds;
  }

  get score(): number {
    return this.totalScore;
  }

  get finished(): boolean {
    return this.completedResult !== null;
  }

  advance(deltaSeconds: number): Readonly<RunSnapshot> {
    const delta = requireNonNegativeFinite(deltaSeconds, "deltaSeconds");
    if (this.completedResult) return this.snapshot();

    this.elapsedSeconds += delta;
    if (this.comboCount > 0) {
      this.comboElapsedSeconds += delta;
      const tolerance = Number.EPSILON * Math.max(1, this.comboElapsedSeconds) * 8;
      if (this.comboElapsedSeconds > COMBO_WINDOW_SECONDS + tolerance) {
        this.comboCount = 0;
        this.comboElapsedSeconds = Number.POSITIVE_INFINITY;
      }
    }

    return this.snapshot();
  }

  scorePickup(basePoints: number, rare = false): Readonly<PickupScore> {
    if (this.completedResult) {
      throw new Error("Cannot score a pickup after the run has finished");
    }

    const normalizedBasePoints = Math.round(
      requireNonNegativeFinite(basePoints, "basePoints"),
    );
    this.comboCount = this.comboCount > 0 ? this.comboCount + 1 : 1;
    this.comboElapsedSeconds = 0;

    const comboMultiplier = Math.min(
      1 + COMBO_MULTIPLIER_STEP * (this.comboCount - 1),
      COMBO_MULTIPLIER_CAP,
    );
    const delta =
      Math.round(normalizedBasePoints * comboMultiplier) +
      (rare ? RARE_BONUS_POINTS : 0);

    this.totalScore += delta;
    this.pickups += 1;
    if (rare) this.rares += 1;

    return Object.freeze({
      basePoints: normalizedBasePoints,
      combo: this.comboCount,
      comboMultiplier,
      rare,
      delta,
      totalScore: this.totalScore,
    });
  }

  finish(): Readonly<RunResult> {
    if (this.completedResult) return this.completedResult;

    const scoreBeforeBonuses = this.totalScore;
    const goalBonus = GOAL_BONUS_POINTS;
    const timeBonus = timeBonusForTime(this.elapsedSeconds);
    this.totalScore += goalBonus + timeBonus;

    this.completedResult = Object.freeze({
      ruleset: COMPETITIVE_RULESET,
      timeSeconds: this.elapsedSeconds,
      rank: rankForTime(this.elapsedSeconds),
      pickupCount: this.pickups,
      rareCount: this.rares,
      scoreBeforeBonuses,
      goalBonus,
      timeBonus,
      score: this.totalScore,
    });
    return this.completedResult;
  }

  snapshot(): Readonly<RunSnapshot> {
    const comboMultiplier =
      this.comboCount === 0
        ? 1
        : Math.min(
            1 + COMBO_MULTIPLIER_STEP * (this.comboCount - 1),
            COMBO_MULTIPLIER_CAP,
          );

    return Object.freeze({
      ruleset: COMPETITIVE_RULESET,
      timeSeconds: this.elapsedSeconds,
      score: this.totalScore,
      pickupCount: this.pickups,
      rareCount: this.rares,
      combo: this.comboCount,
      comboMultiplier,
      finished: this.completedResult !== null,
    });
  }
}
